import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { Trans } from '@lingui/macro'
import { FC, useState } from 'react'
import { ContactFields } from './ContactFields'
// import Loading from 'egret/components/EgretLoadable/Loading'
import { ContactParsed } from 'app/services/sfAuth/sfData/sfContact'
import { AlertBanner } from 'app/views/common/alerts/AlertBanner'
import CustomButton from '../CustomButton'
import { ContactDialogType } from './useContactDialog'
import { isNil } from 'lodash'

interface ContactFieldsCreateProps {
  loading: boolean
  onCreate: ({ contact, isValid }: { contact: ContactParsed, isValid: boolean }) => Promise<void>
  onBack: () => void
  organizationName: string
  type?: ContactDialogType
  role?: string
}

export const ContactFieldsCreate: FC<ContactFieldsCreateProps> = ({
  loading,
  onCreate,
  onBack,
  organizationName,
  type,
  role
}) => {
  const [isValid, setIsValid] = useState(true)
  const [contactConsentConfirmed, setContactConsentConfirmed] = useState(false)
  return (
    <ContactFields
      initialValues={{
        providedOrganizationName: organizationName
      }}
      hiddenFields={{
        providedOrganizationName: type === 'project-affiliations',
        jobTitle: type === 'project-affiliations',
        changeReason: true
      }}
      requiredFields={{
        firstName: true,
        lastName: true,
        email: true,
        phoneNumber: true,
        phoneNumberCountryCode: true,
        jobTitle: true,
        providedOrganizationName: true
      }}
      readonlyFields={{
        firstName: loading,
        lastName: loading,
        email: loading,
        phoneNumber: loading,
        phoneNumberCountryCode: loading,
        jobTitle: loading,
        providedOrganizationName: loading
      }}
      title={
        <>
          <Box
            sx={{
              marginTop: '18px',
              marginBottom: '16px',
              fontWeight: 'bold',
              fontSize: 24,
              lineHeight: '28px',
              color: 'primary.main',
              textAlign: 'center'
            }}
          >
            {type === 'project-affiliations' && !isNil(role)
              ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Trans>PROJECT_AFFILIATIONS_ADD_TITLE</Trans>
                  <Box>{role}</Box>
                </Box>)
              : <Trans>ADD_LEGAL_CONTACT_TITLE</Trans>}
          </Box>
          {!isValid && (
            <AlertBanner
              title={<Trans>MISSING_INFORMATION_BANNER_TITLE</Trans>}
              description={<Trans>MISSING_INFORMATION_BANNER_DESCRIPTION</Trans>}
            />
          )}
          <Typography
            variant='h2'
            sx={{
              fontSize: 20,
              lineHeight: '25px',
              fontWeight: 700,
              paddingBottom: '24px',
              paddingTop: '16px',
              color: 'primary.main'
            }}
          >
            <Trans>CREATE_NEW_CONTACT_TITLE</Trans>
          </Typography>
          <Typography
            variant='h6'
            style={{
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 600,
              marginBottom: '-24px'
            }}
          >
            *<Trans>REQUIRED_ASTERIX_DESCRIPTION</Trans>
          </Typography>
        </>
      }
    >
      {({ values, isValid, touched, setTouched }) => {
        const fieldsTouched = Object.values(touched).some(Boolean)
        const isValidated = fieldsTouched && isValid
        const onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void = e => {
          const checked = e.target.checked
          setContactConsentConfirmed(checked)
          setIsValid(isValidated)
          if (!isValidated) {
            // Move to the top of the form (scroll the dialog content, not the window)
            const dialogContent = document.querySelector('.MuiDialogContent-root')
            dialogContent?.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
            // Touch all fields to show errors
            setTouched({
              firstName: true,
              lastName: true,
              email: true,
              phoneNumber: true,
              phoneNumberCountryCode: true,
              jobTitle: true,
              providedOrganizationName: true
            }).catch(err => console.log(err))
          }
        }

        const handleOnCreate: () => Promise<void> = async () => {
          await onCreate({ contact: values, isValid: isValidated })
        }

        return (
          <>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', marginTop: '-8px', gap: '2px', marginLeft: 0, marginBottom: 0 }}
              control={
                <Checkbox
                  checked={contactConsentConfirmed}
                  onChange={onCheckboxChange}
                  size='small'
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: '20px',
                    paddingTop: '4px'
                  }}
                >
                  {'* '}<Trans>CONTACT_CONSENT_CHECKBOX_DESCRIPTION</Trans>
                </Typography>
              }
            />
            <Box sx={{ display: 'flex', gap: '16px', marginTop: '32px' }}>
              <Button
                disabled={loading || !isValidated || !contactConsentConfirmed}
                fullWidth
                color='primary'
                variant='contained'
                onClick={() => { handleOnCreate().catch(err => console.error(err)) }}
              >
                <Trans>Create new contact</Trans>
              </Button>
              <CustomButton
                variant='outlined'
                sx={{ height: '35px' }}
                onClick={() => {
                  onBack()
                }}
                disabled={loading}
              >
                <Trans>BACK_BTN</Trans>
              </CustomButton>
            </Box>
            {/* {loading && (
              <div style={{ marginTop: 15, height: 100 }}>
                <Loading isNotFixed />
              </div>
            )} */}
          </>
        )
      }}
    </ContactFields>
  )
}
